import { useEffect, useRef, useState } from "react";
import { CheckCircle, Tag, X } from "lucide-react";
import aboutImg from "../../assets/about-hero.jpeg";

const highlights = [
  "Trusted women-owned community pharmacy in Essex County, NJ",
  "Personalized care with a friendly neighborhood approach",
  "Genuine medicines from licensed suppliers",
  "Inclusive, accessible, and welcoming to everyone",
];

export default function About() {
  const ref = useRef(null);
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("visible")),
      { threshold: 0.15 }
    );
    const items = ref.current?.querySelectorAll(".animate-on-view");
    items?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" data-testid="about-section" className="ez-section bg-white" ref={ref}>
      <div className="ez-container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">

          {/* LEFT IMAGE */}
          <div className="animate-on-view relative">
            <div className="relative overflow-hidden rounded-[2rem] rounded-br-[5rem] shadow-2xl bg-gray-900 flex items-center justify-center">
              <img
                src={aboutImg}
                alt="EZ Care Pharmacy — Best of BusinessRate 2025"
                className="w-full h-[500px] object-contain"
              />
            </div>

            {/* Floating stat card */}
            <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-xl p-5 text-center min-w-[140px]">
              <div className="text-4xl font-bold font-outfit text-ez-teal">6+</div>
              <div className="text-sm text-ez-muted mt-1 font-jakarta">Years of Trusted Care</div>
            </div>

            {/* Accent shape */}
            <div className="absolute -top-6 -left-6 w-24 h-24 bg-teal-50 rounded-2xl -z-10" />
          </div>

          {/* RIGHT CONTENT */}
          <div className="animate-on-view" style={{ transitionDelay: "0.2s" }}>
            <div className="inline-block text-ez-teal font-semibold font-caveat text-lg mb-3">
              Who We Are
            </div>
            <h2 className="font-outfit text-2xl md:text-3xl font-bold text-ez-text leading-tight mb-6">
              A Pharmacy Built on
              <br />
              <span className="text-ez-teal">Trust & Community</span>
            </h2>

            <p className="text-ez-muted text-base leading-relaxed mb-4 font-jakarta">
              Founded and led by women, EZ Care Pharmacy puts people first —
              offering fast, affordable prescriptions alongside the kind of
              one-on-one attention you rarely find at chain pharmacies. We proudly
              serve East Orange, the Oranges, Newark, Irvington, and the
              surrounding Essex County neighborhoods.
            </p>

            {/* Lowest Prices Promo */}
            <div className="relative mb-6">
              <button
                onClick={() => setShowPopup(true)}
                className="group flex items-center gap-3 w-full bg-gradient-to-r from-teal-50 to-blue-50 border-2 border-dashed border-ez-teal rounded-2xl px-5 py-4 hover:from-teal-100 hover:to-blue-100 transition-all duration-300 cursor-pointer text-left"
              >
                <div className="relative flex-shrink-0">
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-ping" />
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full" />
                  <Tag size={22} className="text-ez-teal" />
                </div>
                <div>
                  <div className="font-bold text-ez-text font-outfit text-sm">Lowest Prices in New Jersey!</div>
                  <div className="text-ez-teal text-xs font-jakarta font-semibold">Click to see our exclusive offer →</div>
                </div>
              </button>

              {/* Popup */}
              {showPopup && (
                <div className="absolute top-0 left-0 right-0 z-50 bg-white rounded-2xl shadow-2xl border border-teal-200 p-6 animate-fadeIn">
                  <button
                    onClick={() => setShowPopup(false)}
                    className="absolute top-3 right-3 text-gray-400 hover:text-gray-700"
                  >
                    <X size={18} />
                  </button>
                  <div className="text-center">
                    <div className="text-3xl mb-2">🎉</div>
                    <h4 className="font-outfit font-bold text-ez-text text-lg mb-1">Lowest Prices in New Jersey</h4>
                    <div className="bg-ez-teal text-white rounded-xl px-4 py-3 mt-3 font-jakarta">
                      <div className="font-bold text-base">1 Month FREE on Most Medications</div>
                      <div className="text-sm opacity-90 mt-1">with a 3-month supply purchase</div>
                    </div>
                    <p className="text-ez-muted text-xs mt-3 font-jakarta">Ask our pharmacist for details. Offer applies to select medications.</p>
                  </div>
                </div>
              )}
            </div>

            <ul className="space-y-3 mb-8">
              {highlights.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-ez-teal flex-shrink-0 mt-0.5" />
                  <span className="text-ez-text text-sm font-jakarta">{item}</span>
                </li>
              ))}
            </ul>

            <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-2xl p-6 border border-teal-100">
              <p className="text-ez-teal font-semibold font-caveat text-xl">
                "Your wellness is our priority."
              </p>
              <p className="text-ez-teal font-semibold font-caveat text-xl mt-2">
                "We welcome you to experience your Friendly Neighborhood Pharmacy."
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
